import React, { useRef } from 'react';
import '../ds.css';

/**
 * Accessible tabs (WAI-ARIA tablist). Arrow keys move between tabs, Home/End jump.
 * items: [{ id, label, count?, icon?, disabled? }]
 * value / onChange: controlled selection.
 * variant: 'underline' (default) | 'segmented'
 * The caller renders the active panel; give it id={`${idPrefix}-panel-${value}`} role="tabpanel".
 */
export default function Tabs({ items = [], value, onChange, variant = 'underline', label = 'Sections', idPrefix = 'tab', className = '' }) {
    const refs = useRef([]);
    const enabled = items.filter((t) => !t.disabled);

    const focusIndex = (i) => {
        const tab = enabled[(i + enabled.length) % enabled.length];
        if (!tab) return;
        onChange && onChange(tab.id);
        const idx = items.findIndex((t) => t.id === tab.id);
        refs.current[idx] && refs.current[idx].focus();
    };

    const onKeyDown = (e) => {
        const current = enabled.findIndex((t) => t.id === value);
        if (e.key === 'ArrowRight') { e.preventDefault(); focusIndex(current + 1); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); focusIndex(current - 1); }
        else if (e.key === 'Home') { e.preventDefault(); focusIndex(0); }
        else if (e.key === 'End') { e.preventDefault(); focusIndex(enabled.length - 1); }
    };

    return (
        <div
            role="tablist"
            aria-label={label}
            className={`ds-tabs ${variant === 'segmented' ? 'ds-tabs--segmented' : ''} ${className}`}
            onKeyDown={onKeyDown}
        >
            {items.map((t, i) => {
                const selected = t.id === value;
                return (
                    <button
                        key={t.id}
                        ref={(el) => { refs.current[i] = el; }}
                        type="button"
                        role="tab"
                        id={`${idPrefix}-${t.id}`}
                        aria-selected={selected}
                        aria-controls={`${idPrefix}-panel-${t.id}`}
                        tabIndex={selected ? 0 : -1}
                        disabled={t.disabled}
                        className="ds-tab"
                        onClick={() => onChange && onChange(t.id)}
                    >
                        {t.icon}
                        {t.label}
                        {t.count !== undefined && t.count !== null && (
                            <span className="yelo-badge yelo-badge--neutral yelo-badge--sm">{t.count}</span>
                        )}
                    </button>
                );
            })}
        </div>
    );
}
